import { useState } from 'react';
import {
  CreateAllergyParams,
  CreateIllnessParams,
  CreateMedicationParams,
  IllnessType,
  MedicalHistory,
} from '@/api/api.schemas';

export interface FamilyDisease {
  id: string;
  text: string;
}

export interface Illness extends CreateIllnessParams {
  id: string;
}

export interface Surgery {
  id: string;
  text: string;
}

function useList<TItem, TInput = TItem>(
  initialList: TItem[],
  options: {
    keySelector: (item: TItem) => string;
    setIsDirty?: (isDirty: boolean) => void;
    createItem?: (input: TInput) => TItem;
  },
) {
  const [items, setItems] = useState<TItem[]>(initialList);

  const add = (input: TInput) => {
    const newItem = options.createItem ? options.createItem(input) : (input as unknown as TItem);
    setItems((prev) => [newItem, ...prev]);
    options.setIsDirty?.(true);
  };

  const remove = (key: string) => {
    setItems((prev) => prev.filter((i) => options.keySelector(i) !== key));
    options.setIsDirty?.(true);
  };

  return [items, add, remove] as const;
}

export function useMedicalHistoryState(initialData?: MedicalHistory) {
  // TODO: use a single form for medical history page and use the form's isDirty state
  const [isDirty, setIsDirty] = useState(false);

  const [medications, addMedication, removeMedication] = useList<CreateMedicationParams>(
    initialData?.medications.map((m) => ({
      externalId: m.doseSpotMedicationId?.toString() ?? '',
      text: m.name ?? '',
    })) ?? [],
    { keySelector: (item) => item.externalId, setIsDirty },
  );

  const [allergies, addAllergy, removeAllergy] = useList<CreateAllergyParams>(
    initialData?.allergies.map((a) => ({
      externalId: a.doseSpotAllergenId?.toString() ?? '',
      text: a.name ?? '',
    })) ?? [],
    {
      keySelector: (item) => item.externalId,
      setIsDirty,
    },
  );

  const [surgeries, addSurgery, removeSurgery] = useList<Surgery, string>(
    initialData?.surgeries.map((surgery, index) => ({
      id: `${index}-${surgery}`,
      text: surgery,
    })) ?? [],
    {
      keySelector: (item) => item.id,
      createItem: (text) => ({ id: `${Date.now()}-${text}`, text }),
      setIsDirty,
    },
  );

  const [familyDiseases, addFamilyDisease, removeFamilyDisease] = useList<FamilyDisease, string>(
    initialData?.familyDiseases.map((disease) => ({
      id: disease,
      text: disease,
    })) ?? [],
    {
      keySelector: (item) => item.id,
      createItem: (text) => ({ id: `${Date.now()}-${text}`, text }),
      setIsDirty,
    },
  );

  const [illnesses, addIllness, removeIllness] = useList<Illness, CreateIllnessParams>(
    initialData?.illnesses.map((illness, index) => ({
      id: `${index}-${illness}`,
      text: illness.name ?? '',
      type: (illness.type as IllnessType) ?? null,
    })) ?? [],
    {
      keySelector: (item) => item.id,
      createItem: (params) => ({
        id: `${Date.now()}-${params.type}-${params.text}`,
        ...params,
      }),
      setIsDirty,
    },
  );

  return {
    medications,
    addMedication,
    removeMedication,
    allergies,
    addAllergy,
    removeAllergy,
    surgeries,
    addSurgery,
    removeSurgery,
    familyDiseases,
    addFamilyDisease,
    removeFamilyDisease,
    illnesses,
    addIllness,
    removeIllness,
    isDirty,
  };
}
